import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { capitalize } from "lodash";
import { useFragment } from "react-relay";

import { useClubIsSellingPackages } from "@/features/Club/hooks/useClubIsSellingPackages";
import { navigationRef_atom } from "@/features/Navigation";
import { sessionCurrentClubSlugAtom } from "@/features/Session";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  EmptyStateIcon,
  MembershipIcon,
  PackagesIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import { PackageType } from "../../CurrentPackagesModal/components/__generated__/ModalLoaderQuery.graphql";
import { PackagesListEmptyState_customer$key } from "./__generated__/PackagesListEmptyState_customer.graphql";

type Props = {
  type: PackageType;
  customerRef?: PackagesListEmptyState_customer$key | null;
};

export const PackagesListEmptyState = ({ type, customerRef }: Props) => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const navigationRef = useAtomValue(navigationRef_atom);
  const clubSlug = useAtomValue(sessionCurrentClubSlugAtom);

  const packageTypeString =
    type === "MEMBERSHIP" ? t`memberships` : t`packages`;

  const club = useFragment(
    PackagesListEmptyStateFragmentString,
    customerRef
  )?.club;

  const isSellingPackages = useClubIsSellingPackages(club);

  const onBrowsePress = () => {
    const screen = type === "MEMBERSHIP" ? "Memberships" : "Packages";

    if (clubSlug && navigationRef?.isReady()) {
      navigationRef.navigate("Club", {
        clubSlug,
        screen: "Store",
        params: { screen },
      });
      return;
    }

    navigation.navigate("Store", { screen });
  };

  return (
    <VStack alignItems="center" gap={4} pb={8} pt={4} px={12}>
      <EmptyStateIcon width={80} height={80} />
      <VStack alignItems="center" gap={6}>
        <Text
          variant="mainSmallSemiBold"
          textAlign="center"
        >{t`You currently have no active or upcoming ${packageTypeString}.`}</Text>
        {isSellingPackages ? (
          <Button
            accessibilityLabel={t`Browse ${capitalize(packageTypeString)}`}
            size="regular"
            variant="secondary"
            onPress={onBrowsePress}
            leftIcon={
              type === "MEMBERSHIP" ? <MembershipIcon /> : <PackagesIcon />
            }
          >{t`Browse ${capitalize(packageTypeString)}`}</Button>
        ) : null}
      </VStack>
    </VStack>
  );
};

const PackagesListEmptyStateFragmentString = graphql`
  fragment PackagesListEmptyState_customer on Customer {
    club {
      ...useClubIsSellingPackages_club
    }
  }
`;
